import React, {useEffect, useRef, useState} from 'react'
import {Formik, Form, Field, ErrorMessage, useFormikContext} from 'formik'
import * as Yup from 'yup'
import {useParams} from 'react-router-dom'

import {
    DigitalniDopFormValues,
    DigitalniDopInitialValues
} from '../../models/DigitalniDopModel'

import PanelIzolacijaDropdown from './components/PanelIzolacijaDropdown/PanelIzolacijaDropdown'
import DistinctPanelTipDropdown from './components/PanelTipDropdown/DisctinctPanelTipDropdown'

import {getAllDopItems, saveDop, fetchPanelDop, updateDop} from '../../api/paneliDop'
import {distinctDebljinaForTipAndIzolacija} from '../../api/paneliSifre'
import KvalitetMaterijalaDropdown from '../../components/KvalitetMaterijalaDropdown/KvalitetMaterijalaDropdown'
import notifyToast from '../../components/Toast/Toast'
import {toast, ToastContainer} from 'react-toastify'
import onChange = toast.onChange;
import {useNavigate} from 'react-router-dom'

const validationSchema = Yup.object({
    IdentifikacijskaOznakaVProizvoda: Yup.string().required('Obavezno polje'),
    PodrucjePrimjene: Yup.string().required('Obavezno polje'),
    Proizvodjac: Yup.string().required('Obavezno polje'),
    SistemiOcjenjivanja: Yup.string().required('Obavezno polje'),
})

const DopMatrixLoader: React.FC<{
    setDopItems: React.Dispatch<React.SetStateAction<any[]>>
    setDebljine: React.Dispatch<React.SetStateAction<string[]>>
}> = ({setDopItems, setDebljine}) => {

    const {values} = useFormikContext<DigitalniDopFormValues>()

    useEffect(() => {

        if (!values.TipPanela || !values.Izolacija) {
            setDopItems([])
            setDebljine([])
            return
        }

        getAllDopItems().then((data: any) => {
            const list = Array.isArray(data) ? data : data.data
            setDopItems(list || [])
        })

        distinctDebljinaForTipAndIzolacija(
            values.TipPanela!,
            String(values.Izolacija)
        ).then((data: any) => {
            const list = Array.isArray(data) ? data : data.data
            const arr = (list || []).map((d: any) => String(d.debljina))
            setDebljine(arr)
        })

    }, [values.TipPanela, values.Izolacija])

    return null
}

const MatrixInitializer: React.FC<{
    dopItems: any[]
    debljine: string[]
}> = ({dopItems, debljine}) => {

    const {values, setFieldValue} = useFormikContext<DigitalniDopFormValues>()
    const initializedRef = useRef(false)

    useEffect(() => {

        if (initializedRef.current) return
        if (!dopItems.length || !debljine.length) return

        const currentMatrix = values.matrix ?? {}
        const nextMatrix: Record<string, Record<string, string>> = {}

        dopItems.forEach((item: any) => {

            const isSection = item.Numeracija === 'VIII'
            if (isSection) return

            const itemKey = String(item.Id)
            nextMatrix[itemKey] = {}

            debljine.forEach((d: string) => {
                nextMatrix[itemKey][d] = currentMatrix?.[itemKey]?.[d] ?? ''
            })

        })

        initializedRef.current = true
        setFieldValue('matrix', nextMatrix, false)

    }, [dopItems, debljine])

    return null
}
const AutoFillNaziv: React.FC = () => {
    const {values, setFieldValue} = useFormikContext<DigitalniDopFormValues>()

    useEffect(() => {
        if (values.TipPanela && values.Izolacija) {
            const tipPanela =
                values.TipPanela === 'FP'
                    ? 'ZP/S1'
                    : values.TipPanela

            setFieldValue(
                'IdentifikacijskaOznakaVProizvoda',
                `Topterm ${tipPanela} ${values.IzolacijaNaziv || ''}`
            )
        }
    }, [values.TipPanela, values.Izolacija, values.IzolacijaNaziv])

    return null
}
const ResolveIzolacijaNaziv: React.FC = () => {
    const {values, setFieldValue} = useFormikContext<DigitalniDopFormValues>()

    useEffect(() => {
        // If naziv already exists → skip
        if (!values.Izolacija || values.IzolacijaNaziv) return

        // 👇 temporary fallback (you can replace with API later)
        const map: Record<string, string> = {
            '1': 'PUR',
            '2': 'PIR',
            '3': 'RW',
            '5': 'ECOPUR',
            '6': 'UNIPUR'
        }

        const naziv = map[String(values.Izolacija)]

        if (naziv) {
            setFieldValue('IzolacijaNaziv', naziv)
        }
    }, [values.Izolacija])

    return null
}
const DigitalniDopDetails: React.FC = () => {

    const {id} = useParams()

    const [dopItems, setDopItems] = useState<any[]>([])
    const [debljine, setDebljine] = useState<string[]>([])
    const [initialValues, setInitialValues] = useState(DigitalniDopInitialValues)
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    useEffect(() => {

        if (!id) {
            setLoading(false)
            return
        }

        fetchPanelDop(Number(id)).then((data: any) => {

            setInitialValues({
                ...data.dop,
                matrix: data.matrix ?? {}
            })

            setLoading(false)

        })

    }, [id])

    const cleanMatrix = (matrix: any) => {

        const cleaned: Record<string, Record<string, string>> = {}

        Object.entries(matrix || {}).forEach(([itemId, cols]) => {

            if (!cols || typeof cols !== 'object') return

            const filteredCols = Object.entries(cols as Record<string, any>).reduce(
                (acc: Record<string, string>, [debljina, value]) => {

                    const normalized = value == null ? '' : String(value).trim()

                    if (normalized !== '') {
                        acc[debljina] = normalized
                    }

                    return acc

                },
                {}
            )

            if (Object.keys(filteredCols).length > 0) {
                cleaned[itemId] = filteredCols
            }

        })

        return cleaned
    }

    const handleSubmit = async (
        values: DigitalniDopFormValues,
        {setSubmitting, resetForm}: any
    ) => {

        try {

            const payload = {
                ...values,
                Status: 1,
                matrix: cleanMatrix(values.matrix)
            }

            let response

            if (id) {
                response = await updateDop(Number(id), payload)
            } else {
                //console.log(payload)
                response = await saveDop(payload)
            }

            if (response?.data?.success){

                notifyToast(true, `DOP uspješno sačuvan`)

                if (!id) {
                    resetForm()
                    setDopItems([])
                    setDebljine([])
                }

                setTimeout(() => {
                    navigate(-1)
                }, 1500) // small delay so user sees toast

            } else {
                notifyToast(false, 'Došlo je do greške')
            }

        } catch (error: any) {

            const message =
                error?.response?.data?.error ||
                error?.message ||
                'Server error'

            notifyToast(false, message)

        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return <div className="p-10 text-center">Učitavanje...</div>
    }
    return (
        <div className='card mb-5 mb-xl-10'>
            <ToastContainer />
            <div className='card-header border-0'>
                <div className='card-title'>
                    <h3 className='fw-bolder'>📄 Digitalni DOP</h3>
                </div>
            </div>

            <div className='card-body'>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >

                    {(formik) => (

                        <Form>
                            <AutoFillNaziv />
                            <ResolveIzolacijaNaziv />
                            <DopMatrixLoader
                                setDopItems={setDopItems}
                                setDebljine={setDebljine}
                            />

                            <MatrixInitializer
                                dopItems={dopItems}
                                debljine={debljine}
                            />

                            <div className='row'>

                                {/* LEFT SIDE FORM */}

                                <div className='col-lg-5'>

                                    {/* Tip panela + izolacija */}

                                    <div className='mb-8'>
                                        <div className='row'>

                                            <div className='col-6'>
                                                <label className='form-label fw-bold'>
                                                    Vrsta panela
                                                </label>

                                                <DistinctPanelTipDropdown
                                                    value={formik.values.TipPanela ?? undefined}
                                                    onChange={(name, value) =>
                                                        formik.setFieldValue('TipPanela', value)
                                                    }
                                                    errors={formik.errors.TipPanela}
                                                    touched={formik.touched.TipPanela}
                                                    selectName='TipPanela'
                                                />
                                            </div>

                                            <div className='col-6'>
                                                <label className='form-label fw-bold'>
                                                    Izolacija
                                                </label>

                                                <PanelIzolacijaDropdown
                                                    value={formik.values.Izolacija ?? undefined}
                                                    onChange={(name, option) => {
                                                        console.log('IZOLACIJA OPTION:', option)

                                                        // CASE 1: dropdown returns object
                                                        if (typeof option === 'object') {
                                                            formik.setFieldValue('Izolacija', option.value ?? option.Id)
                                                            formik.setFieldValue('IzolacijaNaziv', option.label ?? option.Naziv)
                                                        }
                                                        // CASE 2: dropdown returns only ID (your case)
                                                        else {
                                                            formik.setFieldValue('Izolacija', option)

                                                            // 👇 VERY IMPORTANT: trigger naziv reset (will be filled later if possible)
                                                            formik.setFieldValue('IzolacijaNaziv', '')
                                                        }
                                                    }}
                                                    errors={formik.errors.Izolacija}
                                                    touched={formik.touched.Izolacija}
                                                    selectName='Izolacija'
                                                />
                                            </div>

                                        </div>
                                    </div>
                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Naziv Dopa
                                        </label>

                                        <Field
                                            name='NazivDopa'
                                            className='form-control form-control-lg form-control-solid'
                                        />

                                        <ErrorMessage name='NazivDopa'/>
                                    </div>
                                    {/* IDENTIFIKACIJA */}

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            1. Jedinstvena identifikacijska oznaka vrste proizvoda{' '}
                                            {formik.values.TipPanela && formik.values.IzolacijaNaziv && (
                                                <span className='text-primary ms-2'>
                                                    (Topterm{' '}
                                                    {formik.values.TipPanela === 'FP'
                                                        ? 'ZP/S1'
                                                        : formik.values.TipPanela}{' '}
                                                    {formik.values.IzolacijaNaziv})
                                                </span>
                                            )}
                                        </label>

                                        <Field
                                            name='IdentifikacijskaOznakaVProizvoda'
                                            className='form-control form-control-lg form-control-solid'
                                        />

                                        <ErrorMessage name='IdentifikacijskaOznakaVProizvoda'/>
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            2. Područje primjene
                                        </label>

                                        <Field
                                            as='textarea'
                                            rows='3'
                                            name='PodrucjePrimjene'
                                            className='form-control form-control-lg form-control-solid'
                                        />

                                        <ErrorMessage name='PodrucjePrimjene'/>
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            3. Proizvođač
                                        </label>

                                        <Field
                                            name='Proizvodjac'
                                            className='form-control form-control-lg form-control-solid'
                                        />

                                        <ErrorMessage name='Proizvodjac'/>
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            4. Ovlašteni zastupnik
                                        </label>

                                        <Field
                                            name='OvlasteniZastupnik'
                                            className='form-control form-control-lg form-control-solid'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            5. Sistem ocjenjivanja
                                        </label>

                                        <Field
                                            name='SistemiOcjenjivanja'
                                            className='form-control form-control-lg form-control-solid'
                                        />

                                        <ErrorMessage
                                            name='SistemiOcjenjivanja'
                                            component='div'
                                            className='text-danger mt-2'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            6a. Usklađeni standard
                                        </label>

                                        <Field
                                            name='UskladjeniStandard'
                                            className='form-control form-control-lg form-control-solid'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Obavještajno tijelo
                                        </label>

                                        <Field
                                            name='ObavjestajnoTijelo'
                                            className='form-control form-control-lg form-control-solid'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Dokument o EU ocjenjivanju
                                        </label>

                                        <Field
                                            name='DokumentOEuOcjenjivanju'
                                            className='form-control form-control-lg form-control-solid'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Deklarisana svojstva
                                        </label>

                                        <Field
                                            name='DeklarisanaSvojstva'
                                            className='form-control form-control-lg form-control-solid'
                                        />
                                    </div>

                                    <div className='separator mt-3 mb-6'></div>

                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Kvalitet materijala
                                        </label>

                                        <KvalitetMaterijalaDropdown
                                            value={formik.values.KvalitetMaterijala ?? undefined}
                                            onChange={(name, value) =>
                                                formik.setFieldValue('KvalitetMaterijala', value)
                                            }
                                            errors={formik.errors.KvalitetMaterijala}
                                            touched={formik.touched.KvalitetMaterijala}
                                            selectName='KvalitetMaterijala'
                                        />
                                    </div>

                                    <div className='mb-8'>
                                        <div className='row'>
                                            <div className='col-6'>
                                                <label className='form-label fw-bold'>
                                                    Broj naloga
                                                </label>

                                                <Field
                                                    name='BrojNaloga'
                                                    className='form-control form-control-lg form-control-solid'
                                                />
                                            </div>
                                            <div className='col-6'>
                                                <label className='form-label fw-bold'>
                                                   Stavka
                                                </label>

                                                <Field
                                                    name='Stavka'
                                                    className='form-control form-control-lg form-control-solid'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='mb-8'>
                                        <label className='form-label fw-bold'>
                                            Jezik DOP-a
                                        </label>

                                        <Field
                                            as='select'
                                            name='Jezik'
                                            className='form-select form-select-lg form-select-solid'
                                        >
                                            <option value=''>Bosanski / English (default)</option>
                                            <option value='De'>German (Deutsch)</option>
                                            <option value='Es'>Spanish (Español)</option>
                                            <option value='Sl'>Slovenian (Slovenščina)</option>
                                        </Field>

                                        <div className='form-text mt-2'>
                                            Zadani jezik je Bosanski / English.
                                            U slučaju da se DOP priprema za drugo tržište, odaberite odgovarajući jezik.
                                        </div>

                                        <ErrorMessage name='Jezik' component='div' className='text-danger mt-2' />
                                    </div>

                                </div>

                                {/* MATRIX */}

                                <div className='col-lg-7'>

                                    {dopItems.length > 0 && debljine.length > 0 && (

                                        <div className='table-responsive'>

                                            <table className='table table-bordered'>

                                                <thead>

                                                <tr>
                                                    <th style={{minWidth: 300}}>Svojstvo</th>

                                                    {debljine.map((d) => (
                                                        <th key={d}>{d}</th>
                                                    ))}
                                                </tr>

                                                </thead>

                                                <tbody>

                                                {dopItems.map((item: any) => {

                                                    const isSection = item.Numeracija === 'VIII'

                                                    return (

                                                        <tr key={item.Id}>

                                                            <td>
                                                                <b>{item.Numeracija}</b> {item.Naziv}
                                                            </td>

                                                            {isSection
                                                                ? <td colSpan={debljine.length}></td>
                                                                : debljine.map((d) => {

                                                                    const fieldName = `matrix.${item.Id}.${d}`

                                                                    return (
                                                                        <td key={d}>
                                                                            <Field
                                                                                name={fieldName}
                                                                                className='form-control form-control-sm'
                                                                            />
                                                                        </td>
                                                                    )
                                                                })
                                                            }

                                                        </tr>

                                                    )

                                                })}

                                                </tbody>

                                            </table>

                                        </div>

                                    )}

                                </div>

                            </div>

                            <div className='d-flex justify-content-end mt-10'>

                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    disabled={formik.isSubmitting}
                                >

                                    {formik.isSubmitting
                                        ? 'Saving...'
                                        : id
                                            ? 'Potvrdi izmjene'
                                            : 'Potvrdi'}

                                </button>

                            </div>

                        </Form>

                    )}

                </Formik>

            </div>

        </div>
    )
}

export default DigitalniDopDetails